import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";

export default function QrPay() {
  const navigate = useNavigate();
  const [qrData, setQrData] = useState("");
  const [parsedData, setParsedData] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    // Cleanup scanner on unmount
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
    };
  }, []);

  const startScanner = () => {
    setScannerActive(true);
    setError("");

    // Wait for DOM to be ready
    setTimeout(() => {
      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10, 
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        false
      );

      html5QrcodeScanner.render(
        (decodedText) => {
          // Success callback
          setQrData(decodedText);
          html5QrcodeScanner.clear().catch(console.error);
          setScannerActive(false);
          scannerRef.current = null;
          // Auto-parse after scanning
          parseScannedQrCode(decodedText);
        },
        (errorMessage) => {
          // Error callback (can be ignored for continuous scanning)
          // console.log("QR Scan error:", errorMessage);
        }
      );

      scannerRef.current = html5QrcodeScanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
      setScannerActive(false);
    }
  };

  const parseScannedQrCode = async (scannedData) => {
    setError("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/QrPayment/parse",
        { qrData: scannedData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setParsedData(response.data);
      setAmount(response.data.amount?.toString() || "");
      setDescription(response.data.description || "");
      alert("QR Code scanned successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid QR code");
    } finally {
      setLoading(false);
    }
  };

  const parseQrCode = async () => {
    if (!qrData.trim()) {
      setError("Please enter QR code data");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/QrPayment/parse",
        { qrData: qrData.trim() },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setParsedData(response.data);
      setAmount(response.data.amount?.toString() || "");
      setDescription(response.data.description || "");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid QR code");
    } finally {
      setLoading(false);
    }
  };

  const makePayment = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!parsedData) {
      setError("Please scan a QR code first");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/QrPayment/pay",
        {
          qrData: qrData.trim(),
          amount: parseFloat(amount),
          description: description || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess(
        `Payment successful! ₦${response.data.amount.toLocaleString()} sent to ${
          response.data.recipient
        }. New Balance: ₦${response.data.newBalance.toLocaleString()}`
      );

      // Reset form after 3 seconds
      setTimeout(() => {
        navigate("/dashboard");
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  // Simulated QR scanner
  const simulateScan = () => {
    const sampleQr = "UPTECH:1942217699:100:John Doe:Payment for services";
    setQrData(sampleQr);
    alert("Sample QR code loaded! Click 'Parse QR Code' to continue.");
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>📷 Pay via QR Code</h1>
        <button onClick={() => navigate("/dashboard")} style={styles.backButton}>
          ← Back
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Scan QR Code</h2>
        <p style={styles.cardDesc}>
          Use your camera to scan a QR code or paste the code manually
        </p>

        {!scannerActive ? (
          <div style={styles.scannerPlaceholder}>
            <div style={styles.scanIcon}>📷</div>
            <p style={styles.scannerText}>Camera QR Scanner</p>
            <button onClick={startScanner} style={styles.startScanButton}>
              📸 Start Camera Scanner
            </button>
          </div>
        ) : (
          <div style={styles.scannerContainer}>
            <div id="qr-reader" style={styles.qrReader}></div>
            <button onClick={stopScanner} style={styles.stopScanButton}>
              ⏹️ Stop Scanner
            </button>
          </div>
        )}

        <div style={styles.divider}>
          <span>OR</span>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Paste QR Code Data</label>
          <textarea
            placeholder="Paste QR code data here (Format: UPTECH:account:amount:name:description)"
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
            style={styles.textarea}
            rows="3"
          />
        </div>

        <div style={styles.buttonGroup}>
          <button
            onClick={parseQrCode}
            disabled={loading || !qrData.trim()}
            style={styles.parseButton}
          >
            {loading ? "Parsing..." : "🔍 Parse QR Code"}
          </button>
          <button onClick={simulateScan} style={styles.sampleButton}>
            📋 Load Sample QR
          </button>
        </div>
      </div>

      {parsedData && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Payment Details</h2>

          <div style={styles.infoBox}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Recipient:</span>
              <span style={styles.infoValue}>{parsedData.fullName}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Account:</span>
              <span style={styles.infoValue}>{parsedData.accountNumber}</span>
            </div>
            {parsedData.description && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>For:</span>
                <span style={styles.infoValue}>{parsedData.description}</span>
              </div>
            )}
          </div>

          <form onSubmit={makePayment} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                Amount (₦) {parsedData.amount && <span style={styles.fixedAmount}>- Fixed by QR</span>}
              </label>
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="1"
                step="0.01"
                required
                disabled={!!parsedData.amount}
                style={styles.input}
              />
              {parsedData.amount && (
                <small style={styles.hint}>
                  This amount is fixed by the recipient and cannot be changed
                </small>
              )}
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description (Optional)</label>
              <input
                type="text"
                placeholder="Payment description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={styles.input}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !amount || parseFloat(amount) <= 0}
              style={styles.payButton}
            >
              {loading ? "Processing..." : `💸 Pay ₦${amount || "0"}`}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  backButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#6c757d",
    color: "white",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  success: {
    backgroundColor: "#efe",
    color: "#3c3",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #cfc",
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "20px",
  },
  cardTitle: {
    fontSize: "22px",
    marginBottom: "10px",
    color: "#333",
  },
  cardDesc: {
    color: "#666",
    marginBottom: "20px",
  },
  scannerPlaceholder: {
    backgroundColor: "#f8f9fa",
    border: "2px dashed #dee2e6",
    borderRadius: "8px",
    padding: "40px",
    textAlign: "center",
    marginBottom: "20px",
  },
  scanIcon: {
    fontSize: "48px",
    marginBottom: "10px",
  },
  scannerText: {
    fontSize: "16px",
    color: "#333",
    marginBottom: "15px",
  },
  startScanButton: {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#28a745",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  scannerContainer: {
    marginBottom: "20px",
  },
  qrReader: {
    border: "2px solid #007bff",
    borderRadius: "8px",
    overflow: "hidden",
  },
  stopScanButton: {
    width: "100%",
    marginTop: "10px",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  divider: {
    textAlign: "center",
    margin: "20px 0",
    position: "relative",
    color: "#999",
  },
  inputGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#333",
  },
  fixedAmount: {
    fontSize: "12px",
    color: "#dc3545",
    fontWeight: "normal",
  },
  textarea: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
    fontFamily: "monospace",
    resize: "vertical",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  hint: {
    display: "block",
    marginTop: "5px",
    fontSize: "12px",
    color: "#666",
    fontStyle: "italic",
  },
  buttonGroup: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: "10px",
  },
  parseButton: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  sampleButton: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    cursor: "pointer",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  infoBox: {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #dee2e6",
  },
  infoLabel: {
    fontWeight: "500",
    color: "#666",
  },
  infoValue: {
    color: "#333",
    fontWeight: "600",
  },
  payButton: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#28a745",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

